import { PRIVATE_MIRROR } from '../lib/feedContext';
/**
 * Сторона ленты: отдать обещание в окно перед тем, как открыть чужую вкладку.
 *
 * Пишем в собственное окно, а не куда-то наружу. Если расширение установлено —
 * его скрипт на нашей странице услышит сообщение и запомнит обещание для
 * открывающейся вкладки. Не установлено — сообщение никто не прочтёт, и это
 * ничего не стоит: ни задержки, ни ошибки в консоли, ни изменения в поведении.
 *
 * Порядок важен. Сообщение уходит ДО `window.open`: вкладка конторы начинает
 * грузиться сразу, и обещание должно уже лежать у расширения к тому моменту,
 * как её содержимое появится.
 */

import { profilesEnvelope, type Profiles } from '../aim/profiles';
import { envelope, PROMISE_PATH, type Promise_ } from './expect';
import { revokeEnvelope } from './revoke';
import type { StakePush } from './stakePush';
import { doorsEnvelope } from './doors';
import { probeEnvelope, roadsEnvelope, secretEnvelope, type RoadsMap } from './roads';
import { rosterEnvelope, type Roster } from './roster';

export function promise(one: Promise_): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(envelope(one), window.location.origin);
  } catch {
    // Расширение — не часть договора ленты. Что бы здесь ни случилось, переход
    // на площадку обязан состояться: человек нажал «вход», а не «проверь».
  }
}

/**
 * Копия обещания на сервер. Полоса в AdsPower не видит session Edge.
 * Звать до навигации в стол. Между двумя window.open — нельзя: жест сгорит.
 */
export async function promiseOnServer(one: Promise_): Promise<void> {
  if (PRIVATE_MIRROR || typeof fetch === 'undefined') return;
  try {
    await fetch(PROMISE_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(one),
      keepalive: true,
    });
  } catch {
    // Сервер молчит — полоса останется серой. Вход важнее сверки.
  }
}

/**
 * Отдать в окно состав ленты: что наверху и сколько на это надо по счетам.
 *
 * Уходит редко и молча — лента перерисовывается по нескольку раз в секунду, а
 * панель читают глазами. Частота ограничена вызывающим (`Feed.tsx`), здесь
 * только отправка: правило «одна форма сообщения — одно место» действует и тут.
 */
export function roster(one: Roster): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(rosterEnvelope(one), window.location.origin);
  } catch {
    // То же правило: расширение не должно уметь мешать ленте работать.
  }
}

/**
 * Отдать в окно профили площадок: где на них живут кэфы.
 *
 * Скачивает лента, а не расширение: сеть расширение не трогает вовсе, кроме
 * биржи, и ради одного JSON это свойство не меняют — оно и есть половина
 * ответа на вопрос «а что оно там читает».
 */
/**
 * Отдать расширению двери касс. `ask` — origin, на который просим доступ
 * сверки: только после жеста (чип, уход из поля, проба), не на каждую букву.
 */
export function doors(map: Record<string, string>, ask?: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(doorsEnvelope(map, ask), window.location.origin);
  } catch {
    // То же правило: расширение не должно уметь мешать ленте работать.
  }
}

/** Карта дорог без пароля: какую контору через какой выход открывать. */
export function roads(map: RoadsMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(roadsEnvelope(map), window.location.origin);
  } catch {
    // Расширение молчит — окна откроются с домашнего адреса.
  }
}

export function roadSecret(venue: string, line: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(secretEnvelope(venue, line), window.location.origin);
  } catch {
    //
  }
}

export function roadProbe(venue: string, nonce = 0): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(probeEnvelope(venue, nonce), window.location.origin);
  } catch {
    //
  }
}

/**
 * Человек зафиксировал сумму — обе ноги одним снимком, без новой попытки купона.
 * Старое расширение конверт не знает и молчит: автооткрытие как было.
 */
/** Полка отпустила вилку — купон этой вкладки больше не кормим. */
export function revokePromises(urls: readonly string[]): void {
  if (typeof window === 'undefined') return;
  const boxed = revokeEnvelope(urls);
  if (boxed.urls.length === 0) return;
  try {
    window.postMessage(boxed, window.location.origin);
  } catch {
    // Расширение молчит — лента всё равно отпускает стол.
  }
}

export function stakePush(one: StakePush): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(one, window.location.origin);
  } catch {
    // То же правило: расширение не должно уметь мешать ленте работать.
  }
}

export function profiles(one: Profiles): void {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage(profilesEnvelope(one), window.location.origin);
  } catch {
    // То же правило.
  }
}
